import React from 'react';
import { graphql } from 'gatsby';
import { mergePrismicPreviewData } from 'gatsby-source-prismic';
import { pageSeoData, IS_BROWSER } from '../utils/functions';
import Layout, { SEO } from '../components/Layout';
import SliceZone from '../components/SliceZone';

const DefaultPage = ({ location, data: staticData }) => {
  const previewData = IS_BROWSER && window.__PRISMIC_PREVIEW_DATA__;
  const data = mergePrismicPreviewData({ staticData, previewData });

  const meta = data.prismicPage;
  const page = data.prismicPage.data;

  return (
    <Layout theme={page.page_theme}>
      <SEO
        title={pageSeoData(meta).title}
        desc={pageSeoData(meta).desc}
        banner={pageSeoData(meta).banner}
        pathname={pageSeoData(meta).pathname}
        firstDate={pageSeoData(meta).firstDate}
        lastDate={pageSeoData(meta).lastDate}
      />
      <SliceZone allSlices={page.body} />
    </Layout>
  );
};

export const query = graphql`
  query DefaultPageQuery($uid: String!) {
    prismicPage(uid: { eq: $uid }) {
      uid
      type
      first_publication_date
      last_publication_date
      data {
        page_theme
        seo_title
        seo_description
        seo_thumbnail {
          url
        }
        body {
          ... on PrismicPageBodyTitleText {
            slice_type
            id
            primary {
              title {
                html
              }
              subtitle {
                html
              }
              description {
                html
              }
              align_text
              button_label
              button_link {
                link_type
                url
                target
                type
                uid
                id
              }
              align_image
              thumbnail {
                alt
                copyright
                url
                dimensions {
                  width
                  height
                }
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                }
              }
              scale_module_text
              scale_module_spacing
              theme_color_dark
              theme_color_light
              theme_module
              video {
                embed_url
                title
                thumbnail_url
                thumbnail_width
                thumbnail_height
                type
              }
            }
            items {
              icon {
                alt
                copyright
                url
                dimensions {
                  width
                  height
                }
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyTitleLogo {
            slice_type
            id
            primary {
              color
              logo {
                url
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                }
                dimensions {
                  width
                  height
                }
                alt
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyText {
            slice_type
            id
            primary {
              text {
                html
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyHeroReviews {
            id
            slice_type
            primary {
              layout
              height
              align_text
              description {
                html
              }
              title {
                html
              }
              subtitle {
                html
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyHeroTweet {
            slice_type
            id
            primary {
              account
              link {
                embed_url
                type
                version
                title
                author_name
                author_url
                provider_name
                provider_url
                thumbnail_url
                thumbnail_width
                thumbnail_height
                html
                name
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyHeroStats {
            id
            slice_type
            primary {
              layout
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_module
              theme_color_light
            }
            items {
              title
              description {
                html
              }
              counter_start
              counter_end
              counter_prefix
              counter_suffix
              color
              icon {
                alt
                dimensions {
                  width
                  height
                }
                url
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    dimensions {
                      width
                      height
                    }
                    url
                    alt
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyHeroVideo {
            slice_type
            id
            primary {
              size
              video {
                embed_url
                title
                thumbnail_url
                thumbnail_width
                thumbnail_height
                type
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyHeroProjects {
            slice_type
            id
            primary {
              project_type
              item_width
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
            items {
              project {
                document {
                  ... on PrismicClient {
                    ...prismicClientProject
                  }
                }
              }
            }
          }

          ... on PrismicPageBodyHeroProfile {
            slice_type
            id
            primary {
              staff {
                document {
                  ...prismicStaffCard
                }
              }
              scale_module_spacing
              scale_module_text
            }
          }

          ... on PrismicPageBodyHeroTestimonial {
            slice_type
            id
            primary {
              link {
                url
                type
                link_type
              }
              content {
                html
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
              author_name
              author_label
              avatar {
                alt
                copyright
                url
                dimensions {
                  width
                  height
                }
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyShopifyHero {
            slice_type
            id
            items {
              product {
                id
              }
            }
            primary {
              layout_type
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyShopifyCollections {
            slice_type
            id
            primary {
              collection_limit
              collection_query
              collection_reverse
              collection_sortkey
              product_query
              product_reverse
              product_sortkey
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyListProjects {
            slice_type
            id
            primary {
              project_type
              item_width
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyListArticles {
            id
            slice_type
            primary {
              item_width
              project_type
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyListAssets {
            id
            slice_type
            primary {
              align_text
              title {
                html
              }
              subtitle {
                html
              }
              description {
                html
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
            items {
              label
              background
              download {
                link_type
                url
                target
              }
              file {
                copyright
                url
                alt
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    dimensions {
                      width
                      height
                    }
                    url
                    alt
                  }
                }
                dimensions {
                  width
                  height
                }
              }
            }
          }
          ... on PrismicPageBodyListColors {
            id
            slice_type
            primary {
              align_text
              title {
                html
              }
              subtitle {
                html
              }
              description {
                html
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
            items {
              name
              value
            }
          }
          ... on PrismicPageBodyListLogosClients {
            slice_type
            id
            items {
              client {
                link_type
                url
                target
                type
                uid
                document {
                  ... on PrismicClient {
                    ...prismicClientProject
                  }
                }
              }
            }
            primary {
              align_text
              description {
                html
              }
              title {
                html
              }
              subtitle {
                html
              }
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyListLogosAny {
            slice_type
            id
            items {
              logo {
                alt
                dimensions {
                  width
                  height
                }
                url
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    dimensions {
                      width
                      height
                    }
                    url
                    alt
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyListFaq {
            slice_type
            id
            items {
              faq_answer {
                html
              }
              faq_question
            }
            primary {
              faq_section
              faq_description
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyListStaff {
            slice_type
            id
            items {
              member {
                document {
                  ... on PrismicStaff {
                    ...prismicStaffCard
                  }
                }
              }
            }
            primary {
              align_text
              description {
                html
              }
              title {
                html
              }
              subtitle {
                html
              }
            }
          }
          ... on PrismicPageBodyListImages {
            slice_type
            id
            primary {
              title {
                html
              }
              subtitle {
                html
              }
              description {
                html
              }
              align_text
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
            items {
              image {
                alt
                dimensions {
                  width
                  height
                }
                url
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    dimensions {
                      width
                      height
                    }
                    url
                    alt
                  }
                }
              }
              caption {
                html
                text
                raw
              }
            }
          }
          ... on PrismicPageBodyListBlocks {
            slice_type
            id
            items {
              heading {
                html
              }
              rich_text {
                html
              }
              theme_module
              button_label
              button_link {
                link_type
                url
                target
                type
                uid
                id
              }
              thumbnail {
                alt
                copyright
                url
                dimensions {
                  width
                  height
                }
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                }
              }
            }
            primary {
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
            }
          }
          ... on PrismicPageBodyListSteps {
            slice_type
            id
            items {
              heading {
                html
              }
              rich_text {
                html
              }
              thumbnail {
                alt
                dimensions {
                  width
                  height
                }
                url
                thumbnails {
                  tiny {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  small {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  medium {
                    url
                    dimensions {
                      width
                      height
                    }
                    alt
                  }
                  large {
                    dimensions {
                      width
                      height
                    }
                    url
                    alt
                  }
                }
              }
              step
            }
            primary {
              scale_module_spacing
              scale_module_text
              theme_color_dark
              theme_color_light
              theme_module
            }
          }
          ... on PrismicPageBodyIframe {
            slice_type
            id
            primary {
              name
              link {
                embed_url
                title
              }
            }
          }
        }
      }
    }
  }
`;

/*

  ... on PrismicPageBodyShopifyHero {
    slice_type
    id
    primary {
      layout_type
    }
    items {
      product {
        link_type
        url
        target
        type
        uid
      }
    }
  }

  ... on PrismicPageBodyHeroProducts {
    slice_type
    id
    primary {
      item_width
      scale_module_spacing
      scale_module_text
      theme_color_dark
      theme_color_light
      theme_module
    }
    items {
      product_id
    }
  }

*/
export default DefaultPage;
